import { formatterId } from '../../../helpers/formatters';

const MESES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

// Los tipos de documento circulan por la aplicación con su sigla; el contrato los
// escribe completos porque así los nombra la ley.
const TIPOS_DOCUMENTO = {
    CC: 'cédula de ciudadanía',
    CE: 'cédula de extranjería',
    TI: 'tarjeta de identidad',
    PA: 'pasaporte',
    PAS: 'pasaporte',
    NIT: 'NIT',
};

// Las fechas circulan como "aaaa-mm-dd", a veces con "/" y sin ceros a la izquierda.
const partesFecha = (value) => {
    if (!value) return {};
    const [anio, mes, dia] = String(value).replace(/\//g, '-').split('-');
    if (!anio || !mes || !dia) return {};
    return { dia: String(Number(dia)), mes: MESES[Number(mes) - 1] || mes, anio };
};

const fechaLarga = (value) => {
    const { dia, mes, anio } = partesFecha(value);
    return dia ? `${dia} de ${mes} de ${anio}` : '';
};

// Los importes del sistema son pesos enteros, y así los escribe la plantilla
// ("$5.000.000"). Los centavos se muestran solo si existen: en un contrato no se
// puede redondear en silencio una cifra que el cliente va a pagar.
const pesos = (value) => {
    if (value !== 0 && !value) return '';
    const numero = Number(value) || 0;
    const entero = Math.trunc(numero);
    const centavos = Math.round(Math.abs(numero - entero) * 100);
    const miles = formatterId(entero);
    return centavos ? `$${miles},${String(centavos).padStart(2, '0')}` : `$${miles}`;
};

// La identificación se separa por miles, como en el resto de los formatos. Un NIT
// con dígito de verificación no es un número: en ese caso se escribe tal cual.
const identificacion = (valor) => {
    const texto = valor || valor === 0 ? String(valor).trim() : '';
    if (!texto) return '';
    const numero = Number(texto);
    return Number.isFinite(numero) ? formatterId(numero) : texto;
};

// Los campos "info*" del tercero llegan como "<id>|<nombre>".
const nombreInfo = (value) => (value ? String(value).split('|')[1] || '' : '');

// Si el dato no está, queda una línea para diligenciar a mano sobre el impreso:
// en la comparecencia un hueco vacío deja frases como "expedida en , domiciliado".
const oLinea = (valor) => valor || '__________';

const nombreTercero = (info) => {
    if (!info) return '';
    if (info.razonSocial) return info.razonSocial;
    return [info.nombre1, info.nombre2, info.apellido1, info.apellido2]
        .filter(Boolean)
        .join(' ')
        .trim();
};

/**
 * Arma los datos del contrato de novación a partir de la solicitud ya liquidada y
 * del registro del cliente, que es de donde salen el lugar de expedición, la ciudad
 * y la dirección de notificación: la solicitud sola no los trae.
 *
 * @param solicitud  detalle de la solicitud (SolicitudDet) con sus préstamos novados
 * @param cliente    tercero del cliente; opcional, los campos que aporta quedan
 *                   vacíos si no se pudo cargar
 * @param fechaFirma fecha de la liquidación, "aaaa-mm-dd"
 */
const buildContratoData = ({ solicitud, cliente, fechaFirma }) => {
    const info = cliente ? cliente.terceroInformacion : null;
    const ubicacion = cliente ? cliente.terceroUbicacion : null;
    const sigla = solicitud.tipoIdCliente || 'CC';

    const obligaciones = (solicitud.prestamos || []).map(prestamo => ({
        numero: prestamo.credito ? String(prestamo.credito) : String(prestamo.prestamo || ''),
        fechaOtorgamiento: fechaLarga(prestamo.fecha),
        capitalOriginal: pesos(prestamo.monto),
        saldo: pesos(prestamo.saldopro),
        saldoValor: prestamo.saldopro || 0,
        // Solo se nombra al titular cuando el crédito novado es de un tercero
        // (tipo 2): en los propios el titular es el mismo deudor que firma.
        titular: prestamo.tipo === 2 ? prestamo.nombreTercero : ''
    }));

    return {
        deudor: {
            nombre: (nombreTercero(info) || solicitud.nomCliente || '').toUpperCase(),
            tipoDocumento: TIPOS_DOCUMENTO[sigla] || sigla,
            tipoDocumentoCorto: sigla === 'CC' ? 'C.C.' : sigla,
            identificacion: identificacion(solicitud.idCliente),
            lugarExpedicion: oLinea(nombreInfo(info?.infoMunicipioExp)),
            ciudad: oLinea(nombreInfo(ubicacion?.infoMunicipioRes)),
            direccion: oLinea(ubicacion?.direccionRes),
            email: oLinea(ubicacion?.correo || solicitud.correo)
        },
        credito: {
            numero: solicitud.idCredito ? String(solicitud.idCredito) : '',
            monto: pesos(solicitud.monto),
            montoValor: solicitud.monto || 0,
            cuotas: solicitud.plazo || '',
            valorCuota: pesos(solicitud.valorCuota),
            // El sistema solo maneja libranza con descuento de nómina mensual:
            // el plazo está en meses y la novedad se reporta mes a mes.
            periodicidad: 'mensual',
            primerVencimiento: fechaLarga(solicitud.fechaCorte)
        },
        obligaciones,
        firma: partesFecha(fechaFirma)
    };
};

export default buildContratoData;
